import { Eyebrow } from '@/components/ui/Eyebrow'
import { HeroDeck } from '@/components/sections/HeroDeck'
import { MagneticButton } from '@/components/motion/MagneticButton'
import { ParallaxLayer } from '@/components/motion/ParallaxLayer'
import { Reveal } from '@/components/motion/Reveal'
import { SplitText } from '@/components/motion/SplitText'
import { useSite } from '@/app/site-context'

interface HeroProps {
  onNavigate: (href: string) => void
}

export function Hero({ onNavigate }: HeroProps) {
  const { dictionary, route, pathFor } = useSite()
  const { hero } = dictionary
  const isHome = route.kind === 'home'
  const anchor = (id: string) => (isHome ? `#${id}` : `${pathFor('home')}#${id}`)

  return (
    <section className="hero" id="home">
      <ParallaxLayer className="hero__ghost" speed={-0.1}>
        <div className="hero__ghost-track">
          {Array.from({ length: 4 }, (_, index) => (
            <span key={index}>DALMOS</span>
          ))}
        </div>
      </ParallaxLayer>
      <ParallaxLayer className="hero__shard hero__shard--one" speed={0.22} rotate={0.03} />
      <ParallaxLayer className="hero__shard hero__shard--two" speed={0.34} />
      <ParallaxLayer className="hero__grid" speed={0.06} />
      <HeroDeck />

      <div className="hero__inner">
        <Reveal variant="clip">
          <Eyebrow>{hero.eyebrow}</Eyebrow>
        </Reveal>

        <h1 className="hero__title">
          <SplitText text={hero.headlineBefore} stagger={62} delay={120} />{' '}
          <SplitText text={hero.headlineAccent} stagger={62} delay={420} accent />
        </h1>

        <Reveal variant="rise" delay={520}>
          <p className="hero__subtitle">{hero.subtitle}</p>
        </Reveal>

        <Reveal variant="rise" delay={640}>
          <div className="hero__actions">
            <MagneticButton
              href={anchor('contact')}
              className="button button--solid"
              onNavigate={isHome ? undefined : onNavigate}
            >
              {hero.ctaPrimary}
            </MagneticButton>
            <MagneticButton
              href={anchor('services')}
              className="button button--ghost"
              strength={0.2}
              onNavigate={isHome ? undefined : onNavigate}
            >
              {hero.ctaSecondary}
            </MagneticButton>
          </div>
        </Reveal>

        <Reveal variant="rise" delay={760}>
          <ul className="hero__chips">
            {hero.chips.map((chip, index) => (
              <li className="chip" key={chip}>
                <span className="chip__index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                {chip}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <div className="hero__scroll-hint" aria-hidden="true">
        <span>{hero.scrollHint}</span>
        <span className="hero__scroll-line" />
      </div>
    </section>
  )
}
